import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import AddIcon from '@material-ui/icons/Add'
import { RouteComponentProps } from 'react-router'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'

import Loading from 'components/Loading'
import useMembersCollection from 'group/hooks/useMembersCollection'
import GroupMembersTab from 'group/components/GroupMembersTab'
import GroupManageTab from 'group/components/GroupManageTab'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  pageIntro: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  description: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#747778',
    margin: 0,
  },
  tabs: {
    background: 'transparent !important',
    minHeight: 'auto',
  },
  tabsBar: {
    display: 'inline-block',
    borderBottom: '1px solid #C4C7C7',
  },
  flexContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 0,
    gap: '50px',
  },
  tab: {
    padding: 0,
    width: 'auto',
    minWidth: 'auto',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#8E9192',
    minHeight: 'auto',
    textTransform: 'none',
  },
  tabSelected: {
    fontWeight: 700,
    color: 'black',
  },
  tabPanel: {
    marginTop: '15px',
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'auto',
  },
}))

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  const classes = useStyles()
  return (
    <div role="tabpanel" hidden={value !== index} className={classes.tabPanel}>
      {value === index && children}
    </div>
  )
}

const getTabProps = (index: number) => ({
  id: `group-detail-tab-${index}`,
  'aria-controls': `group-detail-tabpanel-${index}`,
})

interface GroupDetailTabsProps extends RouteComponentProps {
  group: any
  onGroupUpdate?: (payload: Record<string, unknown>) => Promise<unknown>
}

export default function GroupDetailTabs({ group, onGroupUpdate, ...routeProps }: GroupDetailTabsProps) {
  const classes = useStyles()
  const [activeTab, setActiveTab] = React.useState(0)
  const [{ members, initiallyLoaded }, { addMemberLegacy, removeMember, refresh }] = useMembersCollection(group)

  const handleInvite = useCallback(() => {
    addMemberLegacy?.()
  }, [addMemberLegacy])

  if (!initiallyLoaded) {
    return <Loading />
  }

  return (
    <div className={classes.root}>
      <div className={classes.pageIntro}>
        <p className={classes.description}>View, manage, and edit this group</p>
        <ContainedButton startIcon={<AddIcon />} onClick={handleInvite}>
          Invite to Group
        </ContainedButton>
      </div>
      <div className={classes.tabsBar}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Group detail tabs"
          className={classes.tabs}
          classes={{ flexContainer: classes.flexContainer }}
          TabIndicatorProps={{ style: { backgroundColor: '#3776CF', height: '2px' } }}
        >
          <Tab
            label="Members"
            {...getTabProps(0)}
            className={classes.tab}
            classes={{ selected: classes.tabSelected }}
          />
          <Tab label="Manage" {...getTabProps(1)} className={classes.tab} classes={{ selected: classes.tabSelected }} />
        </Tabs>
      </div>
      <TabPanel value={activeTab} index={0}>
        <GroupMembersTab
          group={group}
          members={members || []}
          removeMember={removeMember!}
          refresh={refresh!}
          {...routeProps}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <GroupManageTab group={group} onGroupUpdate={onGroupUpdate} />
      </TabPanel>
    </div>
  )
}
